import styled from 'styled-components';
import { ADMIN_COLORS } from '../../../theme';

export const C = {
  text: ADMIN_COLORS.text,
  secondary: ADMIN_COLORS.textMuted,
  tertiary: ADMIN_COLORS.textFaint,
  separator: ADMIN_COLORS.divider,
  border: ADMIN_COLORS.border,
  fill: ADMIN_COLORS.hover,
  fillHover: ADMIN_COLORS.segmentBg,
  card: ADMIN_COLORS.surface,
  accent: ADMIN_COLORS.accent,
  accentSoft: ADMIN_COLORS.accentSoft,
  success: ADMIN_COLORS.success,
  successSoft: ADMIN_COLORS.successSoft,
  danger: ADMIN_COLORS.danger,
  dangerSoft: ADMIN_COLORS.dangerSoft,
};

export const TILE = {
  indigo: '#5856d6',
  blue: '#007aff',
  orange: '#ff9500',
  green: '#34c759',
  red: '#ff3b30',
  teal: '#30b0c7',
  pink: '#ff2d55',
  gray: '#8e8e93',
};

export const EditorRoot = styled.div`
  margin: 2px 0 24px;
  color: ${C.text};
  font-size: 13px;
  -webkit-font-smoothing: antialiased;

  &&& button {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    z-index: auto !important;
    font-family: inherit;
  }

  &&& .sb-pill-input {
    width: auto !important;
    height: 30px;
    padding: 0 8px !important;
    border: none !important;
    border-radius: 8px !important;
    background: ${C.fill} !important;
    box-shadow: none !important;
    color: ${C.text} !important;
    font-size: 13px !important;
    font-variant-numeric: tabular-nums;
    transition: background 0.15s ease, box-shadow 0.15s ease;
  }

  &&& input[type='time'].sb-pill-input {
    width: 88px !important;
    padding: 0 6px !important;
    text-align: center;
  }

  &&& input[type='time'].sb-pill-input::-webkit-calendar-picker-indicator {
    display: none;
  }

  &&& .sb-pill-input:hover {
    background: ${C.fillHover} !important;
  }

  &&& .sb-pill-input:focus,
  &&& .sb-plain-select:focus {
    box-shadow: 0 0 0 3px ${C.accent}26 !important;
  }

  &&& .sb-text-input {
    width: 100% !important;
    height: 34px !important;
    padding: 0 10px !important;
    border: 1px solid ${C.border} !important;
    border-radius: 8px !important;
    background: ${C.card} !important;
    box-shadow: none !important;
    color: ${C.text} !important;
    font-size: 13px !important;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &&& .sb-text-input::placeholder {
    color: ${C.tertiary};
  }

  &&& .sb-text-input:focus {
    border-color: ${C.accent} !important;
    box-shadow: 0 0 0 3px ${C.accent}26 !important;
    outline: none;
  }

  &&& .sb-pill-input.invalid {
    background: ${C.dangerSoft} !important;
    box-shadow: 0 0 0 1px ${C.danger}66 !important;
    color: ${C.danger} !important;
  }

  &&& .sb-plain-select {
    width: auto !important;
    max-width: 190px;
    height: 32px !important;
    padding: 0 26px 0 10px !important;
    border: none !important;
    border-radius: 8px !important;
    background-color: ${C.fill} !important;
    background-position: right 9px center !important;
    box-shadow: none !important;
    font-size: 12.5px !important;
    text-overflow: ellipsis;
  }
`;

export const GroupBlock = styled.div`
  margin-top: 14px;
`;

export const GroupHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin: 8px 4px 7px;

  .sb-group-title {
    color: ${C.tertiary};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`;

export const GroupCard = styled.div`
  overflow: hidden;
  border: 1px solid ${C.border};
  border-radius: 12px;
  background: ${C.card};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

export const GroupFooter = styled.div`
  margin: 7px 4px 0;
  color: ${C.tertiary};
  font-size: 11.5px;
  line-height: 1.5;

  &.danger {
    color: ${C.danger};
  }

  code {
    padding: 1px 4px;
    border-radius: 4px;
    background: ${C.fill};
    color: ${C.secondary};
    font-size: 11px;
  }
`;

export const Row = styled.div<{ $stacked?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: ${(p) => (p.$stacked ? 'column' : 'row')};
  align-items: ${(p) => (p.$stacked ? 'stretch' : 'center')};
  gap: ${(p) => (p.$stacked ? '8px' : '12px')};
  min-height: 48px;
  padding: 10px 14px;

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 14px;
    height: 1px;
    background: ${C.separator};
  }

  .sb-row-main {
    display: flex;
    align-items: center;
    gap: 11px;
    flex: ${(p) => (p.$stacked ? 'none' : '1 1 auto')};
    min-width: 0;
  }

  .sb-row-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .sb-row-label {
    color: ${C.text};
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.3;
  }

  .sb-row-description {
    color: ${C.tertiary};
    font-size: 11.5px;
    line-height: 1.4;
  }

  .sb-row-description code {
    padding: 1px 4px;
    border-radius: 4px;
    background: ${C.fill};
    color: ${C.secondary};
    font-size: 11px;
  }

  .sb-row-control {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
  }
`;

export const DayRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 8px 14px;

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 14px;
    height: 1px;
    background: ${C.separator};
  }

  .sb-day-name {
    width: 34px;
    flex-shrink: 0;
    color: ${C.text};
    font-size: 13.5px;
    font-weight: 600;
  }

  .sb-day-name.off {
    color: ${C.tertiary};
    font-weight: 500;
  }

  .sb-day-times {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    flex: 1;
    min-width: 0;
    color: ${C.tertiary};
  }

  .sb-day-off {
    color: ${C.tertiary};
    font-size: 12.5px;
  }
`;

export const IconTile = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${(p) => p.$color};
  color: #fff;
`;

export const Segmented = styled.div`
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: 11px;
  background: ${C.separator};
`;

export const SegmentedOption = styled.button<{ $active: boolean }>`
  &&& {
    flex: 1;
    gap: 7px;
    height: 34px;
    padding: 0 10px;
    border: none;
    border-radius: 8px;
    background: ${(p) => (p.$active ? C.card : 'transparent')};
    box-shadow: ${(p) =>
      p.$active ? '0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 0.5px rgba(0, 0, 0, 0.04)' : 'none'};
    color: ${(p) => (p.$active ? C.text : C.secondary)};
    font-size: 13px;
    font-weight: ${(p) => (p.$active ? 600 : 500)};
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
  }

  &:hover {
    color: ${C.text};
  }
`;

export const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  height: 30px;
  border-radius: 8px;
  background: ${C.fill};

  &&& button {
    width: 28px;
    height: 30px;
    padding: 0;
    border: none;
    background: transparent;
    color: ${C.secondary};
    cursor: pointer;
  }

  &&& button:hover:not(:disabled) {
    color: ${C.text};
  }

  &&& button:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &&& input[type='number'] {
    width: 38px !important;
    height: 30px;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: ${C.text} !important;
    font-size: 13px !important;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: center;
    -moz-appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }

  .sb-stepper-unit {
    min-width: 26px;
    padding-right: 2px;
    color: ${C.tertiary};
    font-size: 12px;
  }
`;

export const TextButton = styled.button<{ $tone?: 'accent' | 'danger' | 'muted' }>`
  &&& {
    gap: 5px;
    height: 26px;
    padding: 0 8px;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: ${(p) => (p.$tone === 'danger' ? C.danger : p.$tone === 'muted' ? C.tertiary : C.accent)};
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  &:hover:not(:disabled) {
    background: ${(p) => (p.$tone === 'danger' ? C.dangerSoft : C.accentSoft)};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const PrimaryButton = styled.button`
  &&& {
    gap: 8px;
    width: 100%;
    height: 38px;
    margin-top: 12px;
    border: 1px solid ${C.border};
    border-radius: 10px;
    background: ${C.card};
    color: ${C.accent};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  &:hover:not(:disabled) {
    border-color: ${C.accent}55;
    background: ${C.accentSoft};
  }

  &:disabled {
    color: ${C.tertiary};
    cursor: progress;
  }
`;

export const StatusBanner = styled.div<{ $ok: boolean }>`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  padding: 11px 12px;
  border-radius: 10px;
  background: ${(p) => (p.$ok ? C.successSoft : C.dangerSoft)};
  color: ${(p) => (p.$ok ? C.success : C.danger)};
  font-size: 12.5px;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }

  .sb-status-body {
    min-width: 0;
    word-break: break-word;
  }

  .sb-status-title {
    font-weight: 600;
  }

  .sb-status-samples {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  .sb-status-sample {
    padding: 2px 7px;
    border-radius: 6px;
    background: ${C.card};
    color: ${C.secondary};
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;
  }
`;

export const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${C.accentSoft} 0%, ${C.card} 100%);

  .sb-summary-title {
    color: ${C.text};
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
  }

  .sb-summary-meta {
    margin-top: 2px;
    color: ${C.secondary};
    font-size: 12px;
  }
`;
